import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getOptimalTextColor } from '../../utils/contrastCalculator';
import { useModelMajorityJudgment, formatMajorityJudgmentTooltip, useGradeDescriptions } from '../../hooks/useMajorityJudgment';
import { MultiColorIonoff } from '../ui/MultiColorIonoff';

interface ModelChipProps {
  modelId: string;
  displayName?: string;
  isActive: boolean;
  backgroundColor: string; // Always required now
  onClick: () => void;
  className?: string;
  isMarginDisabled?: boolean; // When true, show gray instead of color
  // Optional pre-fetched majority judgment data (to avoid redundant listeners)
  majorityJudgment?: {
    userJudgment: any;
    globalJudgment: any;
  } | null;
  // For ionoff chips: array of tag colors for the logo
  tagColors?: string[];
  // View toggle: show model names vs creator names
  showModelNames?: boolean;
  modelName?: string | null; // The friendly model name from AA
  onDoubleClick?: () => void;
  // When true, display full openrouterId without extraction (for message titles)
  showFullId?: boolean;
  // When false, chip is non-interactive (no hover effects, no click action)
  interactive?: boolean;
  // Help tooltip support
  helpText?: string | null; // Help text to display when ? is clicked
  onHelpViewed?: () => void; // Callback when help is viewed
  showHelpIcon?: boolean; // Whether to show the help icon
}

/**
 * Truncate model name to first 3 chunks after creator
 * Example: "google/gemini-2.5-flash-lite-preview" -> "gemini-2.5-flash..."
 * Preserves /ionoff/ and priority prefix if present: "/ionoff/speed/google/gemini..." -> "/ionoff/speed/gemini-2.5-flash..."
 */
function truncateModelName(name: string): string {
  // Check for /ionoff/ prefix
  let ionoffPrefix = '';
  let nameWithoutIonoff = name;
  if (name.startsWith('/ionoff/')) {
    ionoffPrefix = '/ionoff/';
    nameWithoutIonoff = name.substring('/ionoff/'.length);
  }
  
  // Check for priority prefix (speed/, balance/, intelligence/, free/, custom/)
  const priorityPrefixes = ['speed/', 'balance/', 'intelligence/', 'free/', 'custom/'];
  let priorityPrefix = '';
  let nameWithoutPriority = nameWithoutIonoff;
  
  for (const prefix of priorityPrefixes) {
    if (nameWithoutIonoff.startsWith(prefix)) {
      priorityPrefix = prefix;
      nameWithoutPriority = nameWithoutIonoff.substring(prefix.length);
      break;
    }
  }
  
  // Remove creator prefix (e.g., "google/")
  const withoutCreator = nameWithoutPriority.includes('/') ? nameWithoutPriority.split('/')[1] : nameWithoutPriority;
  
  // Split by dashes
  const chunks = withoutCreator.split('-');
  
  // If 3 or fewer chunks, return as-is (with prefixes if present)
  if (chunks.length <= 3) {
    return ionoffPrefix + priorityPrefix + withoutCreator;
  }
  
  // Take first 3 chunks and add ellipsis (with prefixes if present)
  return ionoffPrefix + priorityPrefix + chunks.slice(0, 3).join('-') + '...';
}

/**
 * Clickable model chip with assigned highlighter color
 * Visual state indicated by opacity and border, not color change
 */
export const ModelChip: React.FC<ModelChipProps> = ({
  modelId,
  displayName,
  isActive,
  backgroundColor,
  onClick,
  className = '',
  isMarginDisabled = false,
  majorityJudgment = undefined,
  tagColors = [],
  showModelNames = false,
  modelName = null,
  onDoubleClick,
  showFullId = false,
  interactive = true,
  helpText = null,
  onHelpViewed,
  showHelpIcon = false,
}) => {
  // Fetch majority judgment data for this model (only if not provided)
  const fetchedData = useModelMajorityJudgment(majorityJudgment === undefined ? modelId : null);
  const majorityData = majorityJudgment !== undefined ? majorityJudgment : fetchedData;
  
  // Fetch grade descriptions for tooltips
  const gradeDescriptions = useGradeDescriptions();
  
  // Help tooltip state
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  
  // Close help tooltip when clicking outside
  useEffect(() => {
    if (!isHelpOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (chipRef.current && !chipRef.current.contains(event.target as Node)) {
        setIsHelpOpen(false);
      }
    };
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsHelpOpen(false);
      }
    };
    
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isHelpOpen]);
  
  // Handle help icon click
  const handleHelpClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isHelpOpen) {
      setIsHelpOpen(true);
      onHelpViewed?.();
    } else {
      setIsHelpOpen(false);
    }
  }, [isHelpOpen, onHelpViewed]);
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't allow clicking if not interactive or margin is disabled
    if (!interactive || isMarginDisabled) {
      return;
    }
    
    console.log('ModelChip clicked:', modelId, 'isActive:', isActive, 'bgColor:', backgroundColor);
    onClick();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onDoubleClick) {
      onDoubleClick();
    }
  };

  // Check if this is the ionoff chip
  // ONLY show logo for versioned IDs (ionoff/ionoff-v*), not plain 'ionoff'
  // Plain 'ionoff' displays as text "test_plain" in the leaderboard
  const isIonoffChip = modelId.startsWith('ionoff/ionoff-v');
  
  // 🧑 Check if this is a human response chip
  const isHumanChip = modelId === '+human' || modelId.startsWith('user_') || 
    (displayName && (displayName === '+human' || displayName === 'you' || displayName.startsWith('user_')));

  // Check if this is "you" or "peer review winner" chip (special styling)
  const isYouChip = displayName === 'you' || modelId === 'you';
  const isPeerReviewWinner = modelId === 'best' || displayName === 'best';
  // Check if this is a synthesis variant (ionoff_new_plain, etc.)
  // Also check for modelId === 'ionoff' which is the renamed ionoff_new_plain from the hook
  const isSynthesisVariant = modelId === 'ionoff' || modelId.startsWith('ionoff_') || modelId.startsWith('ionoff-grading-');

  // Use gray when margin is disabled, otherwise use the assigned color
  // 🧑 For human chips, use a blue background to distinguish them
  // For "you", "peer review winner", and synthesis variants, use black background with lighter gray text
  const effectiveBackgroundColor = isMarginDisabled 
    ? '#9ca3af' 
    : (isYouChip || isPeerReviewWinner || isSynthesisVariant)
      ? '#000000' // Black for "you", "peer review winner", and synthesis variants
    : isHumanChip 
        ? '#3b82f6' // Blue for other human chips
      : backgroundColor;
  
  // For "peer review winner" and synthesis variants, use lighter gray text (not white)
  // For "you" chip, use optimal text color for good contrast
  const textColor = (isPeerReviewWinner || isSynthesisVariant)
    ? '#9ca3af' // Lighter gray text for special chips
    : getOptimalTextColor(effectiveBackgroundColor);
  
  // Build tooltip text - show full openrouterId (extracted from modelId/displayName)
  // Remove /ionoff/, priority prefix and (high) suffix to get pure openrouterId
  const getOpenRouterId = () => {
    const fullName = displayName || modelId;
    let cleanName = fullName;
    
    // Remove /ionoff/ prefix if present
    if (cleanName.startsWith('/ionoff/')) {
      cleanName = cleanName.substring('/ionoff/'.length);
    }
    
    // Remove priority prefix if present
    const priorityPrefixes = ['speed/', 'balance/', 'intelligence/', 'free/', 'custom/'];
    for (const prefix of priorityPrefixes) {
      if (cleanName.startsWith(prefix)) {
        cleanName = cleanName.substring(prefix.length);
        break;
      }
    }
    
    // Remove (high) suffix if present
    return cleanName.replace(/ \(high\)$/, '');
  };
  
  // Build rich tooltip with majority judgment data if available
  const fullTooltip = isMarginDisabled 
    ? 'Model disabled - enable margin to toggle highlighting' 
    : majorityData?.userJudgment || majorityData?.globalJudgment
      ? formatMajorityJudgmentTooltip(majorityData.userJudgment, majorityData.globalJudgment, gradeDescriptions)
    : getOpenRouterId();
  
  const ringClasses = isIonoffChip
    ? ''
    : isActive && !isMarginDisabled
      ? 'ring-2 ring-offset-2 ring-black dark:ring-white shadow-lg'
      : (isYouChip || isPeerReviewWinner || isSynthesisVariant)
        ? 'ring-1 ring-black/20 dark:ring-white/20' // Same as default for "you", "peer review winner", and synthesis variants
      : isHumanChip
          ? 'ring-2 ring-blue-500 dark:ring-blue-400' // Distinct ring for other human chips
        : 'ring-1 ring-black/20 dark:ring-white/20';

  // Determine display text based on showModelNames toggle
  const fullName = displayName || modelId;
  
  // 🧑 For human chips, show "+human" for global, "you" for explicit "you" display, or "human" for user-level
  if (isHumanChip) {
    const displayText = fullName === 'you' ? 'you' : (modelId === '+human' || fullName === '+human' ? '+human' : 'human');
    // Human chips don't flip
    return (
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`
          color-preserve
          inline-block rounded text-xs font-medium
          transition-all duration-200
          whitespace-nowrap overflow-hidden text-ellipsis
          ${isMarginDisabled ? 'cursor-not-allowed opacity-50' : !interactive ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
          ${ringClasses}
          px-2 py-0.5
          ${className}
        `}
        style={{
          backgroundColor: effectiveBackgroundColor,
          color: textColor,
          WebkitForcedColorAdjust: 'none',
          forcedColorAdjust: 'none',
          colorScheme: 'normal',
        } as React.CSSProperties}
        title={fullTooltip}
        disabled={isMarginDisabled}
      >
        {displayText}
      </button>
    );
  }
  
  // For special chips (you, peer review winner, and synthesis variants)
  if (isYouChip || isPeerReviewWinner || isSynthesisVariant) {
    const displayText = fullName === 'best' ? 'peer review winner' : fullName;
    return (
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`
          color-preserve
          inline-block rounded text-xs font-medium
          transition-all duration-200
          whitespace-nowrap overflow-hidden text-ellipsis
          ${isMarginDisabled ? 'cursor-not-allowed opacity-50' : !interactive ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
          ${ringClasses}
          px-2 py-0.5
          ${className}
        `}
        style={{
          backgroundColor: effectiveBackgroundColor,
          color: textColor,
          WebkitForcedColorAdjust: 'none',
          forcedColorAdjust: 'none',
          colorScheme: 'normal',
        } as React.CSSProperties}
        title={fullTooltip}
        disabled={isMarginDisabled}
      >
        {displayText}
      </button>
    );
  }
  
  // For ionoff chips
  if (isIonoffChip) {
    return (
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`
          color-preserve
          inline-block rounded text-xs font-medium
          transition-all duration-200
          whitespace-nowrap overflow-hidden text-ellipsis
          ${isMarginDisabled ? 'cursor-not-allowed opacity-50' : !interactive ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
          ${ringClasses}
          px-1 py-0.5
          ${className}
        `}
        style={{
          backgroundColor: 'transparent',
          color: 'inherit',
          WebkitForcedColorAdjust: 'none',
          forcedColorAdjust: 'none',
          colorScheme: 'normal',
        } as React.CSSProperties}
        title={fullTooltip}
        disabled={isMarginDisabled}
      >
        {tagColors.length > 0 ? (
          <MultiColorIonoff colors={tagColors} className="text-[0.65rem]" />
        ) : (
          fullName
        )}
      </button>
    );
  }
  
  // For regular model chips: extract creator and model parts
  const openRouterId = getOpenRouterId();
  const parts = openRouterId.split('/');
  const creatorPart = parts.length > 1 ? parts[0] : openRouterId;
  
  // For modelPart, preserve the full name INCLUDING parentheticals (reasoning, high, low, etc.)
  // This is important for message titles which should show the complete model identifier
  const fullNameForParts = displayName || modelId;
  let cleanNameForModelPart = fullNameForParts;
  
  // Only strip /ionoff/ and priority prefixes for extraction, but keep parentheticals
  if (cleanNameForModelPart.startsWith('/ionoff/')) {
    cleanNameForModelPart = cleanNameForModelPart.substring('/ionoff/'.length);
  }
  const priorityPrefixes = ['speed/', 'balance/', 'intelligence/', 'free/', 'custom/'];
  for (const prefix of priorityPrefixes) {
    if (cleanNameForModelPart.startsWith(prefix)) {
      cleanNameForModelPart = cleanNameForModelPart.substring(prefix.length);
      break;
    }
  }
  
  const partsWithParentheticals = cleanNameForModelPart.split('/');
  const modelPart = partsWithParentheticals.length > 1 ? partsWithParentheticals[1] : '';
  
  // Extract /ionoff/ and priority prefix if present (for showFullId mode)
  let ionoffPrefix: string | null = null;
  let priorityPrefix: string | null = null;
  let displayTextWithoutPrefixes = fullName;
  
  if (showFullId && fullName.includes('/')) {
    const parts = fullName.split('/');
    // Check for /ionoff/ prefix
    if (parts[0] === '' && parts[1] === 'ionoff') {
      ionoffPrefix = '/ionoff/';
      // Remove /ionoff/ from the remaining parts
      const remainingParts = parts.slice(2);
      const remainingName = remainingParts.join('/');
      
      // Check if next part is a priority setting (speed, intelligence, balance, free, custom)
      if (remainingParts.length > 0) {
        const firstRemainingPart = remainingParts[0];
        const prioritySettings = ['speed', 'intelligence', 'balance', 'free', 'custom'];
        if (prioritySettings.includes(firstRemainingPart)) {
          priorityPrefix = firstRemainingPart + '/';
          displayTextWithoutPrefixes = remainingParts.slice(1).join('/');
        } else {
          displayTextWithoutPrefixes = remainingName;
        }
      }
    } else {
      // Legacy format: check if first part looks like a priority setting
      const firstPart = parts[0];
      const prioritySettings = ['speed', 'intelligence', 'balance', 'free', 'custom'];
      if (prioritySettings.includes(firstPart)) {
        priorityPrefix = firstPart + '/';
        displayTextWithoutPrefixes = fullName.substring(priorityPrefix.length);
      }
    }
  }
  
  // Determine what to display based on showFullId and showModelNames toggles
  let displayText: string;
  if (showFullId) {
    // Show full openrouterId (without prefixes, which are rendered separately)
    displayText = displayTextWithoutPrefixes;
  } else if (showModelNames) {
    // Show model name (lowercase) if available, otherwise model part
    if (modelName) {
      // Strip creator prefix from aaModelName (e.g., "google: gemini..." -> "gemini...")
      const cleanModelName = modelName.includes(':') 
        ? modelName.split(':')[1].trim() 
        : modelName;
      displayText = cleanModelName.toLowerCase();
    } else {
      displayText = modelPart;
    }
  } else {
    // Show creator part (default - for header chips)
    displayText = creatorPart;
  }

  // If showFullId and we have prefixes, render them separately in gray
  if (showFullId && (ionoffPrefix || priorityPrefix)) {
    const prefixText = ionoffPrefix && priorityPrefix 
      ? `${ionoffPrefix}${priorityPrefix}`
      : ionoffPrefix || priorityPrefix || '';
    return (
      <span className="inline-flex items-center gap-0">
        <span className="text-xs text-muted-foreground font-normal">
          {prefixText}
        </span>
        <button
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          className={`
            color-preserve
            inline-block rounded text-xs font-medium
            transition-all duration-200
            whitespace-nowrap overflow-hidden text-ellipsis
            ${isMarginDisabled ? 'cursor-not-allowed opacity-50' : !interactive ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
            ${ringClasses}
            px-2 py-0.5
            ${className}
          `}
          style={{
            backgroundColor: effectiveBackgroundColor,
            color: textColor,
            WebkitForcedColorAdjust: 'none',
            forcedColorAdjust: 'none',
            colorScheme: 'normal',
          } as React.CSSProperties}
          title={fullTooltip}
          disabled={isMarginDisabled}
        >
          {displayText}
        </button>
      </span>
    );
  }

  // Determine if we should show the help icon for this chip
  const shouldRenderHelp = showHelpIcon && helpText && !showFullId;

  return (
    <button
      ref={chipRef}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={`
        color-preserve
        inline-flex items-center gap-0.5 rounded text-xs font-medium
        transition-all duration-200
        whitespace-nowrap overflow-hidden text-ellipsis
        ${isMarginDisabled ? 'cursor-not-allowed opacity-50' : !interactive ? 'cursor-default' : 'cursor-pointer hover:scale-105'}
        ${ringClasses}
        ${isIonoffChip ? 'px-1 py-0.5' : 'px-2 py-0.5'}
        ${className}
        relative
      `}
      style={{
        backgroundColor: isIonoffChip ? 'transparent' : effectiveBackgroundColor,
        color: isIonoffChip ? 'inherit' : textColor,
        // Force color preservation - resist browser dark mode filters
        WebkitForcedColorAdjust: 'none',
        forcedColorAdjust: 'none',
        colorScheme: 'normal',
      } as React.CSSProperties}
      title={fullTooltip}
      disabled={isMarginDisabled}
    >
      {isIonoffChip && tagColors.length > 0 ? (
        <MultiColorIonoff colors={tagColors} className="text-[0.65rem]" />
      ) : (
        <>
          {displayText}
          {shouldRenderHelp && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleHelpClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHelpClick(e as unknown as React.MouseEvent);
                }
              }}
              className={`
                w-3 h-3 text-[9px]
                rounded-full border
                inline-flex items-center justify-center
                transition-colors duration-150
                cursor-help
                ml-0.5
                opacity-70 hover:opacity-100
              `}
              style={{
                borderColor: `${textColor}80`,
                color: textColor,
              }}
              aria-label="Show help"
              aria-expanded={isHelpOpen}
            >
              <span className="font-medium leading-none">?</span>
            </span>
          )}
        </>
      )}
      
      {/* Help tooltip popup */}
      {isHelpOpen && helpText && (
        <span
          role="tooltip"
          className={`
            absolute z-50
            left-full ml-2 top-1/2 -translate-y-1/2
            max-w-xs sm:max-w-sm
            px-3 py-2
            bg-popover text-popover-foreground
            text-xs leading-relaxed
            rounded-md shadow-lg
            border border-border
            animate-in fade-in-0 zoom-in-95 duration-150
            whitespace-normal
            pointer-events-auto
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {helpText}
        </span>
      )}
    </button>
  );
};

